/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { HttpParams } from "@angular/common/http";
import { of, throwError } from "rxjs";
import { AdvancedFilters } from "../../../../models/http/dashboard/advanced-filters.model";
import { IctCarbonInsightsReportParams } from "../../../../models/http/dashboard/ict-carbon-insights-report.model";
import { ApplicationError } from "../../../../models/ui/application-error.model";
import { HttpRequestOptions } from "../../../../models/ui/http-request-options.model";
import {
	GET_APPS_PERFORMANCE_RESPONSE,
	GET_APPS_WATER_CONSUMPTION_RESPONSE,
	GET_ENVIRONMENTAL_IMPACT_BY_APPLICATION_RESPONSE,
	GET_EXPORT_CSV_RESPONSE,
	GET_EXPORT_MANIFEST_RESPONSE,
	GET_EXPORT_REPORT_RESPONSE,
	GET_TOTAL_CO2_RESPONSE,
	GET_TOTAL_EMISSIONS_RESPONSE
} from "./dashboard-software-assets.db";

const CLASS_NAME = "DashboardSoftwareAssets";

export class DashboardSoftwareAssets {
	public static getAppsPerformance(options: HttpRequestOptions) {
		const filters = options.body as AdvancedFilters | undefined;

		if (!filters?.startDate || !filters.endDate)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						CLASS_NAME,
						"getAppsPerformance"
					)
			);

		return of(GET_APPS_PERFORMANCE_RESPONSE);
	}

	public static getAppsWaterConsumption(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() =>
					new ApplicationError(
						"No params provided",
						CLASS_NAME,
						"getAppsWaterConsumption"
					)
			);

		const startDate = httpParams.get("startDate");
		const endDate = httpParams.get("endDate");
		const applicationId = httpParams.get("applicationId");
		const layerId = httpParams.get("layerId");
		const tenantId = httpParams.get("tenantId");

		if (!startDate || !endDate || !applicationId || !layerId || !tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						CLASS_NAME,
						"getAppsWaterConsumption"
					)
			);

		return of(GET_APPS_WATER_CONSUMPTION_RESPONSE);
	}

	public static getTotalCo2(options: HttpRequestOptions) {
		const filters = options.body as AdvancedFilters | undefined;

		if (!filters?.startDate || !filters.endDate)
			return throwError(
				() => new ApplicationError("Missing required parameters", CLASS_NAME, "getTotalCo2")
			);

		return of(GET_TOTAL_CO2_RESPONSE);
	}

	public static getTotalEmissions(options: HttpRequestOptions) {
		const httpParams = options.params as HttpParams;

		if (!httpParams?.get("startDate"))
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						CLASS_NAME,
						"getTotalEmissions"
					)
			);

		return of(GET_TOTAL_EMISSIONS_RESPONSE);
	}

	public static exportCsv(options: HttpRequestOptions) {
		const filters = options.body as AdvancedFilters | undefined;

		if (!filters?.startDate || !filters.endDate)
			return throwError(
				() => new ApplicationError("Missing required parameters", CLASS_NAME, "exportCsv")
			);

		return of(GET_EXPORT_CSV_RESPONSE);
	}

	public static exportReport(options: HttpRequestOptions) {
		const reportParams = options.body as IctCarbonInsightsReportParams | undefined;

		if (!reportParams)
			return throwError(
				() => new ApplicationError("No params provided", CLASS_NAME, "exportReport")
			);

		const { from, to } = reportParams;

		if (!from || !to)
			return throwError(
				() =>
					new ApplicationError("Missing required parameters", CLASS_NAME, "exportReport")
			);

		const pdfBytes = Uint8Array.from(atob(GET_EXPORT_REPORT_RESPONSE), character =>
			character.charCodeAt(0)
		);

		return of(new Blob([pdfBytes], { type: "application/pdf" }));
	}

	public static exportManifest(options: HttpRequestOptions) {
		const { params } = options;

		const httpParams = params as HttpParams;

		if (!httpParams)
			return throwError(
				() => new ApplicationError("No params provided", CLASS_NAME, "exportManifest")
			);

		const startDate = httpParams.get("startDate");
		const endDate = httpParams.get("endDate");
		const applicationId = httpParams.get("applicationId");
		const tenantId = httpParams.get("tenantId");

		if (!startDate || !endDate || !applicationId || !tenantId)
			return throwError(
				() =>
					new ApplicationError(
						"Missing required parameters",
						CLASS_NAME,
						"exportManifest"
					)
			);

		const yamlText = atob(GET_EXPORT_MANIFEST_RESPONSE);
		return of(new Blob([yamlText], { type: "text/yaml;charset=utf-8" }));
	}

	public static getEnvironmentalImpactByApplication(_options: HttpRequestOptions) {
		return of(GET_ENVIRONMENTAL_IMPACT_BY_APPLICATION_RESPONSE);
	}
}
